import { useEffect, useRef } from 'react';
import { useText } from '@src/components';
import { useLanguageChange } from '@src/hooks/useLanguageChange';

/**
 * Localized weekday labels for the vehicle limit screens.
 *
 * Those screens keep the *selected* days as the labels themselves and turn them back into the
 * backend's `1010100` usable-days mask by each label's position in this list. A language switch
 * therefore leaves the stored selection pointing at labels that no longer exist: the day buttons
 * look unselected, and the next toggle rebuilds the mask without the days that were already
 * selected.
 *
 * `onLanguageChange` is called with a `remapDays(days)` helper right after the language changed -
 * it maps a selection that was made in the previous language onto the current labels, so the
 * caller can move its own state over. The helper leaves anything it cannot map untouched.
 */
export const useDaysOfWeek = (onLanguageChange) => {
  const monday = useText('monday');
  const tuesday = useText('tuesday');
  const wednesday = useText('wednesday');
  const thursday = useText('thursday');
  const friday = useText('friday');
  const saturday = useText('saturday');
  const sunday = useText('sunday');

  const daysOfWeek = [monday, tuesday, wednesday, thursday, friday, saturday, sunday];

  const previousDaysOfWeek = useRef(daysOfWeek);
  const onLanguageChangeRef = useRef(onLanguageChange);
  onLanguageChangeRef.current = onLanguageChange;

  // Registered before the effect below on purpose: it has to read the *previous* language's
  // labels, which that effect is about to overwrite. They are captured here rather than read
  // through the ref, so the helper keeps working if the caller holds on to it.
  useLanguageChange(() => {
    const previous = previousDaysOfWeek.current;
    const current = daysOfWeek;
    const remapDays = (days) => {
      if (!Array.isArray(days)) return days;
      return days.map((day) => {
        const dayIndex = previous.indexOf(day);
        return dayIndex === -1 ? day : current[dayIndex];
      });
    };
    onLanguageChangeRef.current?.(remapDays);
  });

  useEffect(() => {
    previousDaysOfWeek.current = daysOfWeek;
  });

  return daysOfWeek;
};

export default useDaysOfWeek;
